<template>
  <v-app>
    <vue-snotify />

    <template v-if="$vuetify.breakpoint.xs">
      <app-navigation-drawer-mobile
        :items="navigationDrawerItemsAuth"
        :navigation-drawer.sync="navigationDrawerMobileVisible"
      />

      <app-toolbar :navigation-drawer.sync="navigationDrawerMobileVisible" />
    </template>

    <app-navigation-drawer
      v-else
      :drawer-mini.sync="navigationDrawerMini"
      :items="navigationDrawerItemsAuth"
      :navigation-drawer.sync="navigationDrawerVisible"
    />

    <v-main class="synthesis">
      <app-newton-header v-if="hasCorporateAgent" />
      <v-container
        class="main-layout-container"
        :class="{'main-layout-container--with-header': hasCorporateAgent}"
        fluid
      >
        <v-row
          class="main-layout-row"
          dense
        >
          <v-col
            class="main-layout-col"
            cols="12"
          >
            <v-card
              class="main-content-card section-bg-gradient rounded-lg pa-0 pa-sm-2"
              flat
            >
              <div class="main-content-body">
                <slot />
              </div>

              <ai-assistant v-if="hasCorporateAgent" />
            </v-card>
          </v-col>
        </v-row>
      </v-container>
    </v-main>

    <confirm-dialog ref="confirmDialog" />
    <error-dialog ref="errorDialog" />
  </v-app>
</template>

<script>
import AppNavigationDrawer       from '@/theme/default/components/app/AppNavigationDrawer'
import ConfirmDialog             from '@/theme/default/components/common/dialog/ConfirmDialog'
import ErrorDialog               from '@/theme/default/components/common/dialog/ErrorDialog'
import GlobalErrorHandler        from '@/mixins/error/globalErrorHandler'
import AppNavigationDrawerMobile from '@/theme/default/components/app/AppNavigationDrawerMobile'
import AppToolbar                from '@/theme/default/components/app/AppToolbar'
import AlertActiveIncidents      from '@/theme/default/views/alerts/mixins/alertActiveIncidents'
import AccountSectionFeaturesEnum from '@/api/enums/AccountSectionFeaturesEnum'
import AppNewtonHeader from '@/theme/default/components/app/AppNewtonHeader'
import AiAssistant from '@/ai/components/AiAssistant'

export default {
  name      : 'DefaultLayout',
  components: {
    AppToolbar,
    AppNavigationDrawerMobile,
    ErrorDialog,
    ConfirmDialog,
    AppNavigationDrawer,
    AppNewtonHeader,
    AiAssistant
  },
  mixins   : [GlobalErrorHandler, AlertActiveIncidents],
  dataStore: { Positions: 'Positions' },
  data     : function () {
    return {
      navigationDrawerVisible      : false,
      navigationDrawerMobileVisible: false,
      navigationDrawerMini         : false
    }
  },
  computed: {
    navigationDrawerItems () {
      return [
        {
          pos          : 'top',
          icon         : '',
          synthesisIcon: this.$t('Route.Dashboard.Icon'),
          text         : this.$t('Route.Dashboard.Title'),
          route        : {
            name : 'Dashboard',
            exact: false
          },
          features: [
            AccountSectionFeaturesEnum.CORPORATE_DASHBOARD,
            AccountSectionFeaturesEnum.BANKING_DASHBOARD
          ],
          bankingOrder: 1
        },
        {
          pos          : 'top',
          icon         : '',
          synthesisIcon: this.$t('Route.BankingAlm.Icon'),
          text         : this.$t('Route.BankingAlm.Title'),
          route        : {
            name : 'BankingAlm',
            exact: false
          },
          features: [
            AccountSectionFeaturesEnum.BANKING_ALM
          ],
          bankingOrder: 2
        },
        {
          pos          : 'top',
          icon         : '',
          synthesisIcon: this.$t('Route.Alerts.Icon'),
          text         : this.$t('Route.Alerts.Title'),
          route        : {
            name : 'Alerts',
            exact: false
          },
          features: [
            AccountSectionFeaturesEnum.CORPORATE_ALERTS
          ],
          bankingOrder: 7
        },
        {
          pos          : 'top',
          icon         : '',
          synthesisIcon: this.$t('Route.Rates.Icon'),
          text         : this.$t('Route.Rates.Title'),
          route        : {
            name : 'Rates',
            exact: false
          },
          features: [
            AccountSectionFeaturesEnum.BANKING_RATES
          ],
          bankingOrder: 3
        },
        {
          pos          : 'top',
          icon         : '',
          synthesisIcon: this.$t('Route.Risks.Icon'),
          text         : this.$t('Route.Risks.Title'),
          route        : {
            name : 'Risks',
            exact: false
          },
          features: [
            AccountSectionFeaturesEnum.CORPORATE_RISK
          ]
        },
        {
          pos          : 'top',
          icon         : '',
          synthesisIcon: this.$t('Route.Risks.Icon'),
          text         : this.$t('Route.Risks.Title'),
          features     : [
            AccountSectionFeaturesEnum.BANKING_RISK
          ],
          bankingOrder: 4,
          children    :
            [
              {
                text    : this.$t('Route.InterestRate.Title'),
                features: [
                  AccountSectionFeaturesEnum.BANKING_RISK
                ],
                children: [
                  {
                    text : 'Net Interest Income',
                    route: {
                      name : 'NII',
                      exact: false
                    },
                    features: [
                      AccountSectionFeaturesEnum.BANKING_RISK
                    ]
                  },
                  {
                    text : 'E. Value of Equity',
                    route: {
                      name : 'EVE',
                      exact: false
                    },
                    features: [
                      AccountSectionFeaturesEnum.BANKING_RISK
                    ]
                  },
                  {
                    text : this.$t('Route.PastAnalyses.Title'),
                    route: {
                      name : 'PastAnalyses',
                      exact: false
                    },
                    features: [
                      AccountSectionFeaturesEnum.BANKING_RISK
                    ]
                  }
                ]
              },
              {
                text : this.$t('Route.RiskLiquidity.Title'),
                route: {
                  name : 'RiskLiquidity',
                  exact: false
                },
                features: [
                  AccountSectionFeaturesEnum.BANKING_RISK
                ]
              }
            ]
        },
        {
          pos          : 'top',
          icon         : '',
          synthesisIcon: this.$t('Route.Hedging.Icon'),
          text         : this.$t('Route.Hedging.Title'),
          route        : {
            name : 'Hedging',
            exact: false
          },
          features: [
            AccountSectionFeaturesEnum.CORPORATE_HEDGING,
            AccountSectionFeaturesEnum.BANKING_HEDGING
          ],
          bankingOrder: 5
        },
        {
          pos          : 'top',
          icon         : '',
          synthesisIcon: this.$t('Route.Industry.Icon'),
          text         : this.$t('Route.Industry.Title'),
          route        : {
            name : 'Industry',
            exact: false
          },
          features: [
            AccountSectionFeaturesEnum.CORPORATE_INDUSTRY
          ]
        },
        {
          pos          : 'top',
          icon         : '',
          synthesisIcon: this.$t('Route.Positions.Icon'),
          text         : this.$t('Route.Positions.Title'),
          route        : {
            name : 'Positions',
            exact: false
          },
          features: [
            AccountSectionFeaturesEnum.CORPORATE_POSITIONS
          ],
          bankingOrder: 6
        },
        {
          pos          : this.$vuetify.breakpoint.xs ? 'top' : 'bottom',
          icon         : '',
          synthesisIcon: this.$t('Route.KnowledgeCenter.Icon'),
          text         : this.$t('Route.KnowledgeCenter.Title'),
          noAuth       : true,
          route        : {
            name : 'KnowledgeCenter',
            exact: false
          }
        },
        {
          pos          : this.$vuetify.breakpoint.xs ? 'top' : 'bottom',
          icon         : '',
          synthesisIcon: this.$t('Route.Help.Icon'),
          text         : this.$t('Route.Help.Title'),
          model        : this.$route.path.includes('/help/'),
          noAuth       : true,
          children     : [
            {
              text : this.$t('Route.HelpSynthesisSupport.Title'),
              route: {
                name : 'SynthesisSupport',
                exact: true
              }
            },
            {
              text : this.$t('Route.HelpMavenAdvisory.Title'),
              route: {
                name : 'MavenAdvisory',
                exact: true
              }
            }
          ]
        },
        {
          pos          : this.$vuetify.breakpoint.xs ? 'bottom' : 'account',
          icon         : '',
          synthesisIcon: this.$t('Route.MyDetails.Icon'),
          text         : this.$t('Route.MyDetails.Title'),
          noAuth       : true,
          route        : {
            name : 'MyDetails',
            exact: false
          }
        },
        {
          pos          : this.$vuetify.breakpoint.xs ? 'bottom' : 'account',
          icon         : '',
          synthesisIcon: this.$t('Route.TermsAndPrivacy.Icon'),
          text         : this.$t('Route.TermsAndPrivacy.Title'),
          noAuth       : true,
          route        : {
            name : 'TermsAndPrivacy',
            exact: false
          }
        }
      ]
    },

    navigationDrawerItemsAuth () {
      const retVal = []

      this.navigationDrawerItems.forEach(item => {
        if (Array.isArray(item)) {
          let tmpc = []
          item.forEach(c => {
            if ((c.hasOwnProperty('divider') ||
              (c.hasOwnProperty('text') && !c.hasOwnProperty('route')) ||
              (c.hasOwnProperty('text') && c.hasOwnProperty('route') && this.userCanAccess) &&
              this.hasFeatures(c))
            ) {
              if (c.hasOwnProperty('children')) {
                const tmp = []
                c.children.forEach(child => {
                  if (child.hasOwnProperty('divider') || child.hasOwnProperty('heading') ||
                    (child.hasOwnProperty('text') && !child.hasOwnProperty('route')) ||
                    (child.hasOwnProperty('text') && child.hasOwnProperty('route') && this.userCanAccess)
                  ) {
                    tmp.push(child)
                  }
                })
                if (tmp.length > 0) {
                  c.children = tmp
                  tmpc.push(c)
                }
              } else {
                tmpc.push(c)
              }
            }
          })
          if (tmpc.length === 1 && tmpc[0].hasOwnProperty('divider')) tmpc = []
          tmpc.forEach(child => retVal.push(child))
        } else {
          if ((item.hasOwnProperty('divider') || item.hasOwnProperty('heading') ||
            (item.hasOwnProperty('text') && !item.hasOwnProperty('route')) ||
            (item.hasOwnProperty('text') && item.hasOwnProperty('route') && this.userCanAccess)) && this.hasFeatures(item)
          ) {
            if (item.hasOwnProperty('children')) {
              const tmp = []
              item.children.forEach(child => {
                if (child.hasOwnProperty('divider') || child.hasOwnProperty('heading') ||
                  (child.hasOwnProperty('text') && !child.hasOwnProperty('route')) ||
                  (child.hasOwnProperty('text') && child.hasOwnProperty('route') && this.userCanAccess)
                ) {
                  tmp.push(child)
                }
              })
              if (tmp.length > 0) {
                item.children = tmp
                retVal.push(item)
              }
            } else {
              retVal.push(item)
            }
          }
        }
      })

      // Sort menu items for banking users
      const isBankingUser = this.user && this.user.hasSectionFeature(AccountSectionFeaturesEnum.BANKING_DASHBOARD)

      if (isBankingUser) {
        // Filter items by position for sorting
        const topItems = retVal.filter(item => item.pos === 'top')
        const bottomItems = retVal.filter(item => item.pos === 'bottom')
        const accountItems = retVal.filter(item => item.pos === 'account')

        // Sort top items by bankingOrder if present, otherwise keep original order
        const sortedTopItems = topItems.sort((a, b) => {
          const orderA = a.bankingOrder || 999
          const orderB = b.bankingOrder || 999
          return orderA - orderB
        })

        return [...sortedTopItems, ...bottomItems, ...accountItems]
      }

      return retVal
    },

    hasCorporateAgent () {
      return this.user && this.user.hasSectionFeature(AccountSectionFeaturesEnum.CORPORATE_AGENT)
    }
  },
  watch: {},
  created () {},
  mounted () {
    this.$root.$confirmDialog = this.$refs.confirmDialog.open
  },
  beforeDestroy () {},
  methods: {
    hasFeatures (item) {
      if (item.noAuth) {
        return true
      }

      return this.user &&
        item &&
        item.features &&
        Array.isArray(item.features) &&
        item.features.length > 0 &&
        item.features.some(feature => this.user.hasSectionFeature(feature))
    }
  }
}

</script>

<style>
.synthesis {
  height: 100vh;
  overflow: hidden;
}

.main-layout-container {
  height: 100vh;
  min-height: 0;
  padding: 6px 6px 6px 0 !important;
  overflow: hidden;
}

.main-layout-container--with-header {
  padding: 0 6px 6px 0 !important;
  height: calc(100vh - 56px);
}

.main-layout-row {
  height: 100%;
  min-height: 0;
  margin: 0;
}

.main-layout-col {
  height: 100%;
  min-height: 0;
  padding: 0;
  overflow: hidden;
}

.main-content-card {
  position: relative;
  height: 100%;
  min-height: 0;
  overflow: hidden;
}

.main-content-body {
  width: 100%;
  height: 100%;
  min-width: 0;
  min-height: 0;
  overflow-x: hidden;
  overflow-y: auto;
}
</style>
